/* Header v2 navigation behaviour.
   Spec: desktop dropdowns close on click outside; the mobile drawer closes via
   its X button. Dropdown triggers are <button>s, so keyboard users get the same
   open/close affordance as pointer users. */
(function () {
  'use strict'

  var DESKTOP_MIN_WIDTH = 1100

  function HeaderV2(root) {
    this.root = root
    this.wrapper = root.closest('.header-v2-wrapper') || root
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-hb-dropdown]'))
    this.drawer = document.querySelector('[data-hb-drawer]')
    this.openItem = null

    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)

    this.bindDropdowns()
    this.bindDrawer()
    this.bindPhone()

    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onKeydown)
  }

  /* ---------- phone reveal ---------- */

  /* Spec: click the handset to reveal the number, click anywhere outside the
     pill to close it, tap the number to dial. The drawer renders its own
     always-open copy, which has no toggle and so is skipped here. */
  HeaderV2.prototype.bindPhone = function () {
    var self = this
    this.phones = Array.prototype.slice.call(document.querySelectorAll('[data-hb-phone]'))

    this.phones.forEach(function (phone) {
      var toggle = phone.querySelector('[data-hb-phone-toggle]')
      if (!toggle) return

      toggle.addEventListener('click', function (event) {
        event.preventDefault()
        event.stopPropagation()
        self.togglePhone(phone)
      })
    })
  }

  HeaderV2.prototype.togglePhone = function (phone) {
    var isOpen = phone.classList.contains('is-open')
    this.closePhones()
    if (!isOpen) this.setPhoneOpen(phone, true)
  }

  HeaderV2.prototype.setPhoneOpen = function (phone, open) {
    var toggle = phone.querySelector('[data-hb-phone-toggle]')
    var link = phone.querySelector('[data-hb-phone-link]')

    phone.classList.toggle('is-open', open)
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false')

    /* Keep the collapsed link out of the tab order and off the a11y tree —
       it is zero-width, so a focus ring on it would land on nothing. */
    if (link) {
      link.setAttribute('aria-hidden', open ? 'false' : 'true')
      link.setAttribute('tabindex', open ? '0' : '-1')
    }
  }

  HeaderV2.prototype.closePhones = function () {
    var self = this
    ;(this.phones || []).forEach(function (phone) {
      if (phone.classList.contains('is-open')) self.setPhoneOpen(phone, false)
    })
  }

  /* ---------- desktop dropdowns ---------- */

  HeaderV2.prototype.bindDropdowns = function () {
    var self = this

    this.items.forEach(function (item) {
      var trigger = item.querySelector('[data-hb-dropdown-trigger]')
      if (!trigger) return

      trigger.addEventListener('click', function (event) {
        if (window.innerWidth < DESKTOP_MIN_WIDTH) return
        event.preventDefault()
        event.stopPropagation()

        if (item === self.openItem) {
          self.closeDropdown()
        } else {
          self.openDropdown(item)
        }
      })
    })
  }

  HeaderV2.prototype.openDropdown = function (item) {
    if (this.openItem && this.openItem !== item) this.closeDropdown()

    var trigger = item.querySelector('[data-hb-dropdown-trigger]')
    item.classList.add('header-v2__item--open')
    if (trigger) trigger.setAttribute('aria-expanded', 'true')
    this.wrapper.classList.add('header-v2-wrapper--open')
    this.openItem = item

    /* Opening search should put the caret straight in the field. The panel
       animates out of `visibility: hidden` and a hidden element cannot take
       focus, so force a style/layout flush to commit the class change before
       focusing - waiting a frame is not enough, the panel is still hidden. */
    var searchInput = item.querySelector('.header-v2__search-input')
    if (searchInput) {
      void item.offsetHeight
      searchInput.focus()
    }
  }

  HeaderV2.prototype.closeDropdown = function () {
    if (!this.openItem) return

    var trigger = this.openItem.querySelector('[data-hb-dropdown-trigger]')
    this.openItem.classList.remove('header-v2__item--open')
    if (trigger) trigger.setAttribute('aria-expanded', 'false')
    this.wrapper.classList.remove('header-v2-wrapper--open')
    this.openItem = null
  }

  /* Spec: "Desktop - click anywhere outside of dropdown to close". The nav bar
     itself counts as outside, so a second click on the trigger toggles (handled
     above via stopPropagation) but a click on the logo or icons closes. */
  HeaderV2.prototype.onDocumentClick = function (event) {
    /* Spec: "click anywhere outside of pill shape to close". A click on the
       number itself is the dial action, so the pill counts as inside. */
    var insidePhone = (this.phones || []).some(function (phone) {
      return phone.contains(event.target)
    })
    if (!insidePhone) this.closePhones()

    if (!this.openItem) return
    if (this.openItem.contains(event.target)) return
    this.closeDropdown()
  }

  HeaderV2.prototype.onKeydown = function (event) {
    if (event.key !== 'Escape') return

    this.closePhones()

    if (this.openItem) {
      var trigger = this.openItem.querySelector('[data-hb-dropdown-trigger]')
      this.closeDropdown()
      if (trigger) trigger.focus()
    }

    if (this.drawer && this.drawer.classList.contains('header-v2-drawer--open')) {
      this.closeDrawer()
    }
  }

  /* ---------- mobile drawer ---------- */

  HeaderV2.prototype.bindDrawer = function () {
    var self = this
    if (!this.drawer) return

    var openers = document.querySelectorAll('[data-hb-drawer-open]')
    Array.prototype.forEach.call(openers, function (opener) {
      opener.addEventListener('click', function (event) {
        event.preventDefault()
        self.openDrawer()
      })
    })

    /* Mobile search is a distinct drawer state (Figma 348:6397): the field
       replaces the menu rather than sitting above it. */
    var searchOpeners = document.querySelectorAll('[data-hb-drawer-search]')
    Array.prototype.forEach.call(searchOpeners, function (opener) {
      opener.addEventListener('click', function (event) {
        event.preventDefault()
        self.openDrawer(true)
      })
    })

    // Spec: "Mob - click x to close".
    var closers = this.drawer.querySelectorAll('[data-hb-drawer-close]')
    Array.prototype.forEach.call(closers, function (closer) {
      closer.addEventListener('click', function (event) {
        event.preventDefault()
        self.closeDrawer()
      })
    })

    this.bindAccordions()
  }

  HeaderV2.prototype.openDrawer = function (searchMode) {
    this.closeDropdown()
    /* The bar's phone control is hidden while the drawer is up (the drawer
       shows its own), so collapse it rather than leaving it open behind. */
    this.closePhones()
    this.drawer.classList.add('header-v2-drawer--open')
    this.drawer.classList.toggle('header-v2-drawer--search', !!searchMode)
    this.drawer.setAttribute('aria-hidden', 'false')
    document.body.classList.add('header-v2-drawer-open')

    if (searchMode) {
      var input = this.drawer.querySelector('.header-v2-drawer__search input[type="search"]')
      if (input) input.focus()
      return
    }

    var closeButton = this.drawer.querySelector('[data-hb-drawer-close]')
    if (closeButton) closeButton.focus()
  }

  HeaderV2.prototype.closeDrawer = function () {
    this.drawer.classList.remove('header-v2-drawer--open')
    this.drawer.classList.remove('header-v2-drawer--search')
    this.drawer.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('header-v2-drawer-open')

    var opener = document.querySelector('[data-hb-drawer-open]')
    if (opener) opener.focus()
  }

  /* Accordion rows: top-level nav sections, "By room"/"By collection" groups,
     and the individual room lists. Each level is exclusive - opening one closes
     its siblings, matching 348:7331 where only LIVING is expanded. */
  HeaderV2.prototype.bindAccordions = function () {
    var levels = [
      {
        toggle: '[data-hb-accordion-toggle]',
        item: '.header-v2-drawer__item',
        openClass: 'header-v2-drawer__item--open',
        listClass: 'header-v2-drawer__menu--has-open',
      },
      {
        toggle: '[data-hb-group-toggle]',
        item: '.header-v2-drawer__group',
        openClass: 'header-v2-drawer__group--open',
      },
      {
        toggle: '[data-hb-room-toggle]',
        item: '.header-v2-drawer__room',
        openClass: 'header-v2-drawer__room--open',
      },
    ]

    levels.forEach(function (level) {
      var toggles = this.drawer.querySelectorAll(level.toggle)

      Array.prototype.forEach.call(toggles, function (toggle) {
        toggle.addEventListener('click', function (event) {
          event.preventDefault()

          var item = toggle.closest(level.item)
          if (!item) return

          var parent = item.parentElement
          var wasOpen = item.classList.contains(level.openClass)

          Array.prototype.forEach.call(parent.children, function (sibling) {
            sibling.classList.remove(level.openClass)
            var siblingToggle = sibling.querySelector(level.toggle)
            if (siblingToggle) siblingToggle.setAttribute('aria-expanded', 'false')
          })

          if (!wasOpen) {
            item.classList.add(level.openClass)
            toggle.setAttribute('aria-expanded', 'true')
          }

          if (level.listClass) {
            parent.classList.toggle(level.listClass, !wasOpen)
          }
        })
      })
    }, this)
  }

  function init() {
    var roots = document.querySelectorAll('[data-hb-header-v2]')
    Array.prototype.forEach.call(roots, function (root) {
      if (root.hbHeaderV2) return
      root.hbHeaderV2 = new HeaderV2(root)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }

  // Theme editor re-renders the section without a full page load.
  document.addEventListener('shopify:section:load', init)
})()
